import {
  Avatar,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  KeyboardArrowUp,
} from "@mui/icons-material";
import { tokens } from "../../../theme";
import { useRef, useState, useEffect, useCallback } from "react";
import MemoryBookReassignModal from "./MemoryBookAssignModal";
import { MemoryBookEntry } from "./MemoryBookCard";
import MemoryBookMinimisedPill from "./MemoryBookMinimisedPill";
import StatusColumn from "./StatusColumn";
import {
  fetchAllMemoryBookAssignments,
  MemoryBookAssignmentApiItem,
  reassignMemoryBook,
} from "../../stream-management/components/memorial-page/comments-compiler/service/memoryBook.api";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

type MemoryBookStatus = "overdue" | "in_danger" | "safe";

interface StaffAssignment {
  id: number;
  avatarUrl?: string;
  name: string;
  assignedCount: number;
}

interface MemoryBookSummaryProps {
  statusCounts?: { inDanger: number; almostDue: number; safe: number };
}

const getDaysRemaining = (dueDate?: string | null): number => {
  if (!dueDate) return 0;
  const due = new Date(dueDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  return Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
};

const getStatus = (dueDate: string): MemoryBookStatus => {
  const days = getDaysRemaining(dueDate);
  if (days < 0) return "overdue";
  if (days <= 3) return "in_danger";
  return "safe";
};

const formatTimeInfo = (dueDate: string): string => {
  const days = getDaysRemaining(dueDate);
  if (days < 0)
    return `${Math.abs(days)} day${Math.abs(days) !== 1 ? "s" : ""} ago`;
  if (days === 0) return "Due today";
  if (days === 1) return "Due tomorrow";
  const due = new Date(dueDate);
  return due.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

const mapAssignmentToEntry = (
  a: MemoryBookAssignmentApiItem,
): MemoryBookEntry => {
  console.log(
    "assignment:",
    a.id,
    "stream_id:",
    a.stream_id,
    "stream?.id:",
    a.stream?.id,
  );
  const deceased = a.stream?.booking?.deceased?.[0];
  const deceasedName = deceased
    ? `${deceased.first_name} ${deceased.surname}`
    : (a.stream?.title ?? "Unknown");

  return {
    id: a.id,
    deceasedName,
    deceasedAvatarUrl: deceased?.person_image,
    currentAssigneeId: a.assigned_to_id,
    currentAssigneeName: a.assigned_to
      ? `${a.assigned_to.first_name} ${a.assigned_to.last_name}`
      : undefined,
    currentAssigneeAvatar: a.assigned_to?.profile_picture_url ?? undefined,
    streamId: a.stream?.id,
    timeInfo: formatTimeInfo(a.due_date),
    commentCount: a.physical_comments ?? 0,
    progressPercent: a.physical_comments
      ? Math.min(
          100,
          Math.round(((a.done_comments ?? 0) / a.physical_comments) * 100),
        )
      : 0,
    status: getStatus(a.due_date),
  };
};

const mapStaffAssignments = (
  results: MemoryBookAssignmentApiItem[],
): StaffAssignment[] => {
  const map = new Map<number, StaffAssignment>();
  results.forEach((a) => {
    const u = a.assigned_to;
    if (!u) return;
    if (!map.has(u.id)) {
      map.set(u.id, {
        id: u.id,
        name: `${u.first_name} ${u.last_name}`,
        avatarUrl: u.profile_picture_url ?? undefined,
        assignedCount: 0,
      });
    }
    map.get(u.id)!.assignedCount += 1;
  });
  return Array.from(map.values());
};

const MemoryBookSummary = ({ statusCounts }: MemoryBookSummaryProps) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);

  const [expanded, setExpanded] = useState(true);
  const [modalEntry, setModalEntry] = useState<MemoryBookEntry | null>(null);
  const [currentAssigneeId, setCurrentAssigneeId] = useState<number | null>(
    null,
  );
  const [entries, setEntries] = useState<MemoryBookEntry[]>([]);
  const [staffAssignments, setStaffAssignments] = useState<StaffAssignment[]>(
    [],
  );
  const [loading, setLoading] = useState(true);

  const loadAssignments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchAllMemoryBookAssignments();
      const results: MemoryBookAssignmentApiItem[] = res.data?.results ?? [];

      const filtered = results.filter((a) => {
        const allDone =
          (a.physical_comments ?? 0) > 0 &&
          (a.done_comments ?? 0) >= (a.physical_comments ?? 0);
        const isDelivered = a.memory_book?.is_delivered === true;
        return !(allDone && isDelivered);
      });

      setEntries(filtered.map(mapAssignmentToEntry));
      setStaffAssignments(mapStaffAssignments(filtered));
    } catch (err) {
      console.error("Failed to fetch memory book assignments:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  const handleReassign = async (entryId: number, staffId: number) => {
    try {
      await reassignMemoryBook(entryId, staffId);
      toast.success("Memory book reassigned successfully!");
      setModalEntry(null);
      loadAssignments();
    } catch {
      toast.error("Failed to reassign memory book.");
    }
  };

  const inDangerCount = entries.filter((e) => e.status === "overdue").length;
  const overdueCount = entries.filter((e) => e.status === "in_danger").length;

  const computedCounts = {
    overdue: inDangerCount,
    inDanger: overdueCount,
    safe: entries.filter((e) => e.status === "safe").length,
    ...(statusCounts ?? {}),
  };

  const scroll = (dir: "left" | "right") => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({
      left: dir === "left" ? -310 : 310,
      behavior: "smooth",
    });
  };

  const grouped = {
    overdue: entries.filter((e) => e.status === "overdue"),
    in_danger: entries.filter((e) => e.status === "in_danger"),
    safe: entries.filter((e) => e.status === "safe"),
  };

  if (!expanded) {
    return (
      <MemoryBookMinimisedPill
        overdueCount={overdueCount}
        inDangerCount={inDangerCount}
        onExpand={() => setExpanded(true)}
      />
    );
  }

  return (
    <>
      <Box
        sx={{
          position: "relative",
          width: "fit-content",
          mx: "auto",
          mb: { xs: 4, md: 6 },
        }}
      >
        <Box
          sx={{
            border: `1px solid ${colors.primary[300]}30`,
            borderRadius: "40px",
            p: { xs: 2, md: 3 },
            backgroundColor: colors.primary[900],
            position: "relative",
          }}
        >
          {/* Minimise button */}
          <Tooltip title="Minimise" arrow>
            <IconButton
              onClick={() => setExpanded(false)}
              size="small"
              sx={{
                position: "absolute",
                top: 12,
                right: 16,
                color: colors.primary[700],
                zIndex: 3,
                "&:hover": { color: colors.primary[400] },
              }}
            >
              <KeyboardArrowUp sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>

          {/* Left nav */}
          <IconButton
            onClick={() => scroll("left")}
            sx={{
              position: "absolute",
              left: { xs: -20, md: -28 },
              top: "50%",
              transform: "translateY(-50%)",
              backgroundColor: colors.primary[400],
              color: "white",
              width: { xs: 32, md: 40 },
              height: { xs: 32, md: 40 },
              zIndex: 2,
              "&:hover": { backgroundColor: colors.primary[300] },
              boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
            }}
          >
            <ChevronLeft />
          </IconButton>

          {/* Right nav */}
          <IconButton
            onClick={() => scroll("right")}
            sx={{
              position: "absolute",
              right: { xs: -20, md: -28 },
              top: "50%",
              transform: "translateY(-50%)",
              backgroundColor: colors.primary[400],
              color: "white",
              width: { xs: 32, md: 40 },
              height: { xs: 32, md: 40 },
              zIndex: 2,
              "&:hover": { backgroundColor: colors.primary[300] },
              boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
            }}
          >
            <ChevronRight />
          </IconButton>

          <Box
            ref={scrollRef}
            sx={{
              display: "flex",
              gap: 2,
              overflowX: "auto",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            <Box
              sx={{
                minWidth: { xs: 240, sm: 260 },
                maxWidth: { xs: 240, sm: 260 },
                backgroundColor: colors.primary[800],
                borderRadius: "30px",
                p: 2.5,
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "0.7rem",
                  letterSpacing: "0.1em",
                  color: colors.primary[700],
                }}
              >
                MEMORY BOOKS SUMMARY
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  mb: 2,
                }}
              >
                {[
                  {
                    label: "Over Due",
                    count: computedCounts.overdue,
                    color: "#E11D48",
                  },
                  {
                    label: "In Danger",
                    count: computedCounts.inDanger,
                    color: "#f59e0b",
                  },
                  {
                    label: "SAFE",
                    count: computedCounts.safe,
                    color: "#10b981",
                  },
                ].map(({ label, count, color }) => (
                  <Box
                    key={label}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor: color,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        sx={{ fontSize: "0.8rem", color: colors.primary[100] }}
                      >
                        {label}
                      </Typography>
                    </Box>
                    <Chip
                      label={count}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.7rem",
                        backgroundColor: colors.primary[700] + "30",
                        color: colors.primary[100],
                        "& .MuiChip-label": { px: 1 },
                      }}
                    />
                  </Box>
                ))}
              </Box>

              {/* Staff assignments */}
              <Box
                sx={{
                  borderRadius: "20px",
                  backgroundColor: colors.primary[900],
                  px: 1.5,
                  py: 2,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: colors.primary[700],
                    mb: 1.5,
                  }}
                >
                  Memory book assignments
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {staffAssignments.map((staff) => (
                    <Tooltip
                      key={staff.id}
                      title={`${staff.name}: ${staff.assignedCount} assigned`}
                      arrow
                    >
                      <Box sx={{ position: "relative", cursor: "pointer" }}>
                        <Avatar
                          src={staff.avatarUrl}
                          sx={{
                            width: 40,
                            height: 40,
                            border: `2px solid ${colors.primary[400]}`,
                          }}
                        >
                          {staff.name[0]}
                        </Avatar>
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: -4,
                            right: -4,
                            width: 18,
                            height: 18,
                            borderRadius: "50%",
                            backgroundColor: "#E11D48",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: `1.5px solid ${colors.primary[900]}`,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "white",
                              fontWeight: 700,
                              lineHeight: 1,
                            }}
                          >
                            {staff.assignedCount}
                          </Typography>
                        </Box>
                      </Box>
                    </Tooltip>
                  ))}
                </Box>
              </Box>
            </Box>
            {(["overdue", "in_danger", "safe"] as MemoryBookStatus[]).map(
              (status) => (
                <StatusColumn
                  key={status}
                  status={status}
                  entries={loading ? [] : grouped[status]}
                  colors={colors}
                  loading={loading}
                  onCardClick={(entry) => {
                    setModalEntry(entry);
                    setCurrentAssigneeId(entry.currentAssigneeId ?? null);
                  }}
                />
              ),
            )}
          </Box>
        </Box>
      </Box>

      <MemoryBookReassignModal
        open={!!modalEntry}
        onClose={() => setModalEntry(null)}
        entry={modalEntry}
        staffOptions={staffAssignments}
        currentAssigneeId={currentAssigneeId ?? 1}
        onReassign={handleReassign}
        onViewBook={() => {
          if (modalEntry) {
            navigate(`/memory-table/view/${modalEntry.streamId}`);
            setModalEntry(null);
          }
        }}
      />
    </>
  );
};

export default MemoryBookSummary;
